import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  ContentLanguage,
  toContentLanguage,
} from 'app/api/models/user/content-language';
import {
  SalesProfileQuestionnaireDto,
  SalesProfileQuestionnaireLanguageDto,
} from 'app/api/models/salesProfile/salesProfileQuestionnaireDto';

const API = '/api/SalesProfileQuestionnaire';

@Injectable({ providedIn: 'root' })
export class SalesProfileQuestionnaireService {
  constructor(private http: HttpClient) {}

  /**
   * The copy the home page renders for the signed-in user. The server resolves
   * it from their content language and falls back to Danish, so the client
   * never has to pick. Null when no language has any copy at all.
   */
  getForMe(): Observable<SalesProfileQuestionnaireDto | null> {
    return this.http
      .get<SalesProfileQuestionnaireDto | null>(`${API}/me`)
      .pipe(map((dto) => this.normalize(dto)));
  }

  /** Tenant overview: which content languages already have questions. */
  getLanguages(
    tenantId: string,
  ): Observable<SalesProfileQuestionnaireLanguageDto[]> {
    return this.http
      .get<
        SalesProfileQuestionnaireLanguageDto[]
      >(`${API}/tenant/${tenantId}/languages`)
      .pipe(
        map((rows) =>
          (rows ?? []).map((row) => ({
            ...row,
            language: toContentLanguage(row.language) ?? ContentLanguage.Danish,
          })),
        ),
      );
  }

  getByLanguage(
    tenantId: string,
    language: ContentLanguage,
  ): Observable<SalesProfileQuestionnaireDto | null> {
    return this.http
      .get<
        SalesProfileQuestionnaireDto | null
      >(`${API}/tenant/${tenantId}/${language}`)
      .pipe(map((dto) => this.normalize(dto)));
  }

  save(
    tenantId: string,
    language: ContentLanguage,
    dto: SalesProfileQuestionnaireDto,
  ): Observable<SalesProfileQuestionnaireDto> {
    return this.http
      .put<
        SalesProfileQuestionnaireDto
      >(`${API}/tenant/${tenantId}/${language}`, dto)
      .pipe(map((saved) => this.normalize(saved)!));
  }

  /**
   * The API serialises enums by name, so the language that comes back is
   * "English"/"Danish" rather than the numeric value the rest of the client
   * works with. Normalise it on the way in.
   */
  private normalize(
    dto: SalesProfileQuestionnaireDto | null,
  ): SalesProfileQuestionnaireDto | null {
    if (!dto) {
      return null;
    }
    return {
      ...dto,
      language: toContentLanguage(dto.language) ?? ContentLanguage.Danish,
    };
  }

  /**
   * Drops one language's questions. Saving an emptied form lands here, so the
   * language goes back to having no copy at all.
   */
  delete(tenantId: string, language: ContentLanguage): Observable<unknown> {
    return this.http.delete(`${API}/tenant/${tenantId}/${language}`);
  }

  /**
   * The wording the app ships with, for filling the form. Reads only — it is
   * stored when the admin saves, not when it is loaded.
   */
  getDefaults(
    language: ContentLanguage,
  ): Observable<SalesProfileQuestionnaireDto | null> {
    return this.http
      .get<SalesProfileQuestionnaireDto | null>(`${API}/defaults/${language}`)
      .pipe(map((dto) => this.normalize(dto)));
  }
}
